import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewEncapsulation,
  inject,
  CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { DateTime } from 'luxon';

import { IonicModule, ModalController } from '@ionic/angular';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import {
  PropertyReviewsService,
  ExpectedReview,
  HostGuestReview,
  HostGuestReviewInput,
} from 'src/shared/services/property-reviews.service';

import { StarRatingComponent, RATING_WORDS } from 'src/shared/components/star-rating/star-rating.component';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

// 'create' = no outbound review yet; 'edit' = a queued/draft/failed review the
// host can still change; 'posted' = already on the platform, read-only.
type LeaveReviewMode = 'create' | 'edit' | 'posted';

// The star categories Airbnb has hosts rate guests on, in display order.
const RATING_CATEGORIES: { key: 'cleanliness' | 'communication' | 'observance_of_house_rules'; label: string }[] = [
  { key: 'cleanliness', label: 'Cleanliness' },
  { key: 'communication', label: 'Communication' },
  { key: 'observance_of_house_rules', label: 'Observance of house rules' },
];

// Airbnb's review window is 14 days from checkout; queued negatives post on
// day 14 (checkout + 13 days) to minimize the guest's retaliation window.
const NEGATIVE_POST_OFFSET_DAYS = 13;

@Component({
  selector: 'leave-guest-review',
  templateUrl: './leave-guest-review.component.html',
  styleUrls: ['./leave-guest-review.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
    StarRatingComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class LeaveGuestReviewComponent implements OnInit {
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private util = inject(Util);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  private modalCtrl = inject(ModalController);
  private propertyReviewsService = inject(PropertyReviewsService);
  private demoBlock = inject(DemoBlockService);
  windowService = inject(WindowService);

  private static readonly PLATFORM_LABELS: Record<string, string> = {
    airbnb: 'Airbnb',
    vrbo: 'VRBO',
    booking_com: 'Booking.com',
  };

  // Brand-tinted chip per platform; empty string falls back to the neutral chip.
  private static readonly PLATFORM_CHIP_CLASSES: Record<string, string> = {
    airbnb: 'platform-chip--airbnb',
    vrbo: 'platform-chip--vrbo',
    booking_com: 'platform-chip--booking',
  };

  readonly categories = RATING_CATEGORIES;

  expected: ExpectedReview | null = null;
  existing: HostGuestReview | null = null;
  mode: LeaveReviewMode = 'create';
  loading = true;
  submitted = false;
  submitting = false;
  channelLabel = '';
  platformChipClass = '';
  stayDates = '';
  negativePostDate = '';
  queuedPostDate = '';
  errorMessage = '';
  // false while no leave-review write is live for the platform: the form is
  // replaced by a notice (a pending review can still be cancelled).
  leaveAvailable = true;
  leaveUnavailableMessage = '';
  // Plain state (not getters) so the OnPush template stays function-free.
  // isQueued: waiting for its day-14 auto-post; isRetryable: a failed (or
  // leftover draft) review the host can fix and post again.
  isQueued = false;
  isRetryable = false;

  // Star ratings live as plain state (not form controls) so the tappable
  // star-rating control can drive them; they always default to 5, so valid.
  ratings: Record<'cleanliness' | 'communication' | 'observance_of_house_rules', number> = {
    cleanliness: 5,
    communication: 5,
    observance_of_house_rules: 5,
  };

  // Word + tint shown beside each category's stars ("Excellent" in green,
  // "Poor" in red), precomputed so the template stays function-free.
  ratingMeta: Record<'cleanliness' | 'communication' | 'observance_of_house_rules', { word: string; cls: string }> = {
    cleanliness: { word: RATING_WORDS[5], cls: 'good' },
    communication: { word: RATING_WORDS[5], cls: 'good' },
    observance_of_house_rules: { word: RATING_WORDS[5], cls: 'good' },
  };

  // Recommend is plain state (two selectable cards, not a radio control).
  recommend = true;

  form = this.fb.group({
    publicText: ['', [Validators.required, Validators.minLength(2)]],
    privateNote: [''],
  });

  setRating(key: 'cleanliness' | 'communication' | 'observance_of_house_rules', value: number): void {
    this.ratings[key] = value;
    this.ratingMeta[key] = this.ratingMetaFor(value);
    this.cdr.markForCheck();
  }

  selectRecommend(value: boolean): void {
    this.recommend = value;
    this.cdr.markForCheck();
  }

  private ratingMetaFor(value: number): { word: string; cls: string } {
    return {
      word: RATING_WORDS[value] || '',
      cls: value >= 4 ? 'good' : value === 3 ? 'mid' : 'low',
    };
  }

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    this.expected = (params?.expected as ExpectedReview) || null;
    this.rightSidePanelService.setDialogPageTitle('Leave Guest Review');

    if (this.expected) {
      this.channelLabel = this.formatPlatform(this.expected.platform);
      this.platformChipClass = LeaveGuestReviewComponent.PLATFORM_CHIP_CLASSES[this.expected.platform] || '';
      this.stayDates = this.formatStayRange(this.expected.checkInDate, this.expected.checkOutDate);
      this.negativePostDate = this.computeNegativePostDate(this.expected.checkOutDate);
      this.leaveAvailable = this.expected.canLeaveReview === true;
      this.leaveUnavailableMessage = `Leaving ${this.channelLabel} guest reviews from TIDY isn't available yet. Please leave this review on ${this.channelLabel} directly.`;
    }
    await this.loadExisting();
    this.loading = false;
    this.cdr.markForCheck();
  }

  // Reopening a reservation must show the review already saved for it (queued,
  // failed or posted) so it stays editable/cancellable across visits.
  private async loadExisting(): Promise<void> {
    if (!this.expected) {
      return;
    }
    try {
      this.applyExisting(await this.propertyReviewsService.getHostGuestReviewForReservation(this.expected.reservationId));
    } catch {
      this.errorMessage = 'Could not load the existing review for this guest. Please reopen to try again.';
    }
  }

  private applyExisting(review: HostGuestReview | null): void {
    this.existing = review;
    this.isQueued = review?.status === 'queued';
    this.isRetryable = review?.status === 'failed' || review?.status === 'draft';
    if (!review) {
      this.mode = 'create';
      return;
    }
    if (review.status === 'posted') {
      this.mode = 'posted';
    } else {
      this.mode = 'edit';
      if (review.status === 'failed' && this.leaveAvailable) {
        this.errorMessage = review.error || 'Posting this review failed. Please try again.';
      }
    }
    const ratings = review.ratings || {};
    this.ratings = {
      cleanliness: ratings.cleanliness ?? 5,
      communication: ratings.communication ?? 5,
      observance_of_house_rules: ratings.observance_of_house_rules ?? 5,
    };
    this.ratingMeta = {
      cleanliness: this.ratingMetaFor(this.ratings.cleanliness),
      communication: this.ratingMetaFor(this.ratings.communication),
      observance_of_house_rules: this.ratingMetaFor(this.ratings.observance_of_house_rules),
    };
    this.recommend = review.recommend ?? true;
    // Prefer the backend's scheduled time over our locally computed estimate.
    this.queuedPostDate = review.post_at
      ? DateTime.fromISO(review.post_at).toFormat('MMM d, yyyy')
      : this.negativePostDate;
    this.form.patchValue({
      publicText: review.public_text || '',
      privateNote: review.private_note || '',
    });
  }

  // Post now (positive) — the immediate path.
  postNow = () => this.submit(false);

  // Queue as negative — auto-posts at day 14.
  queueNegative = () => this.submit(true);

  private async submit(queueAsNegative: boolean) {
    if (this.demoBlock.block()) { return; }
    this.submitted = true;
    this.errorMessage = '';
    if (this.form.invalid || !this.expected || this.submitting) {
      return;
    }
    this.submitting = true;
    this.cdr.markForCheck();
    const input = this.buildInput(queueAsNegative);
    try {
      const result = await this.save(input, queueAsNegative);

      if (result?.status === 'posted') {
        this.util.showSuccess('Review posted to the platform.');
        this.close();
        return;
      }
      // A platform rejection comes back as a 2xx with status 'failed', so it
      // must not read as success: stay in edit mode with the error and retry.
      this.applyExisting(result);
      this.submitting = false;
      if (result?.status === 'queued') {
        this.util.showSuccess(`Review queued. It posts on ${this.queuedPostDate}.`);
      } else if (result?.status !== 'failed') {
        this.util.showSuccess('Review saved.');
      }
      this.cdr.markForCheck();
    } catch (err: any) {
      this.submitting = false;
      this.errorMessage = err?.error?.message || err?.message || 'Failed to save review. Please try again.';
      this.cdr.markForCheck();
    }
  }

  // New review → leave (post now or queue). Existing one → save the edits,
  // then, unless it is only an update to a queued review, post it now (the
  // retry for a failed review).
  private async save(input: HostGuestReviewInput, queueAsNegative: boolean): Promise<HostGuestReview> {
    if (this.mode !== 'edit' || !this.existing) {
      return this.propertyReviewsService.leaveGuestReview(input);
    }
    const updated = await this.propertyReviewsService.updateHostGuestReview(this.existing.id, input);
    if (queueAsNegative && updated.status === 'queued') {
      return updated;
    }
    return this.propertyReviewsService.repostHostGuestReview(updated.id);
  }

  private buildInput(queueAsNegative: boolean): HostGuestReviewInput {
    const value = this.form.value;
    return {
      guestReservationId: this.expected!.reservationId,
      ratings: {
        cleanliness: this.ratings.cleanliness,
        communication: this.ratings.communication,
        observance_of_house_rules: this.ratings.observance_of_house_rules,
      },
      publicText: (value.publicText || '').trim(),
      privateNote: (value.privateNote || '').trim(),
      recommend: this.recommend,
      queueAsNegative,
    };
  }

  cancelQueued = async () => {
    if (this.demoBlock.block()) { return; }
    if (!this.existing || this.submitting) {
      return;
    }
    const params: ConfirmPageParamsModel = {
      title: this.isQueued ? 'Cancel Queued Review?' : 'Cancel Review?',
      body: 'This deletes the review so it will not post to the platform. You can leave a new review afterwards.',
      backText: 'Go Back',
      confirmText: 'Cancel Review',
      confirmAction: this.confirmCancelQueued,
    };
    const modal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await modal.present();
  };

  private confirmCancelQueued = async () => {
    try {
      await this.propertyReviewsService.cancelHostGuestReview(this.existing!.id);
    } catch (err: any) {
      await this.modalCtrl.dismiss();
      this.util.showError(err?.error?.message || err?.message || 'Failed to cancel the review. Please try again.', 10000);
      return;
    }
    this.util.showSuccess('Review cancelled.');
    await this.modalCtrl.dismiss();
    this.close();
  };

  private computeNegativePostDate(checkOutDate: string): string {
    const checkout = checkOutDate ? DateTime.fromISO(checkOutDate) : null;
    if (!checkout?.isValid) {
      return 'day 14 of the review window';
    }
    return checkout.plus({ days: NEGATIVE_POST_OFFSET_DAYS }).toFormat('MMM d, yyyy');
  }

  private formatStayRange(checkIn: string, checkOut: string): string {
    const start = checkIn ? DateTime.fromISO(checkIn) : null;
    const end = checkOut ? DateTime.fromISO(checkOut) : null;
    if (start?.isValid && end?.isValid) {
      return `${start.toFormat('MMM d')} - ${end.toFormat('MMM d, yyyy')}`;
    }
    if (end?.isValid) {
      return end.toFormat('MMM d, yyyy');
    }
    return checkOut || checkIn || '';
  }

  private formatPlatform(platform: string): string {
    return LeaveGuestReviewComponent.PLATFORM_LABELS[platform] ?? (platform || '');
  }

  // A plain history back strands the host when the page was opened directly
  // (no history to pop), so always return to the Reviews tab.
  topNavBack = (): boolean => {
    this.close();
    return false;
  };

  private close() {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateBack('revenue?tab=reviews');
    }
  }
}
